import type { SpecificationItemDto } from "generated/models";

export interface SpecAttribute {
  label: string;
  values: string[];
}

export interface SpecSubGroup {
  subHeading?: string;
  attributes: SpecAttribute[];
}

export interface SpecSection {
  heading: string;
  groups: SpecSubGroup[];
}

const GENERAL_SECTION = "General";

/**
 * Internal identifier fields returned by the catalog feed that are not useful to a
 * customer browsing specs (they duplicate header/quick-details data or are opaque IDs).
 */
const NOISE_LABELS = new Set([
  "ProductName",
  "Description",
  "ProductImageTag",
  "ProductImageTagType",
  "ProductId",
  "ProductLine",
  "ProductDescriptor",
  "MfgPartNumAbbre",
  "ProductType",
]);

const CUSTOMER_HIDDEN_TERMS = [
  "selling program",
  "buying program",
  "topseller",
];

function isMeaningfulValue(value: string | null | undefined): value is string {
  if (value == null) return false;
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed !== "-";
}

export function normalizeCatalogText(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

export function isCustomerVisibleCatalogText(
  value: string | null | undefined,
): value is string {
  if (!isMeaningfulValue(value)) return false;

  const normalized = value.toLowerCase();
  return !CUSTOMER_HIDDEN_TERMS.some((term) => normalized.includes(term));
}

function isCustomerHiddenSpec(item: SpecificationItemDto): boolean {
  const searchableParts = [item.groupName, item.displayName, item.displayValue]
    .filter((part): part is string => part != null)
    .map((part) => part.toLowerCase());

  return CUSTOMER_HIDDEN_TERMS.some((term) =>
    searchableParts.some((part) => part.includes(term)),
  );
}

/**
 * Groups the flat `fullSpecification` list into ordered, deduplicated spec sections with subgroups.
 *
 * - Within each section, labels are bucketed into subgroups: if label contains " / ", split into subHeading + label.
 * - Labels without " / " land in a single unnamed group (subHeading: undefined). Group ordering follows first-seen order in the input.
 * - Semicolon-delimited values are expanded into a deduplicated value list.
 */
export function fullSpecificationToSections(
  items: SpecificationItemDto[] | null | undefined,
): SpecSection[] {
  if (!items || items.length === 0) return [];

  const sectionOrder: string[] = [];
  type SubGroupMap = Map<string | undefined, Map<string, Set<string>>>;
  const sections = new Map<string, SubGroupMap>();

  for (const item of items) {
    const displayName = item.displayName?.trim();
    if (!displayName || NOISE_LABELS.has(displayName)) continue;
    if (!isMeaningfulValue(item.displayValue)) continue;
    if (isCustomerHiddenSpec(item)) continue;

    const groupName = item.groupName?.trim();
    let heading: string;
    let remainder: string;

    if (groupName) {
      heading = groupName;
      remainder = displayName;
    } else {
      const separatorIndex = displayName.indexOf(" / ");
      if (separatorIndex >= 0) {
        heading = displayName.slice(0, separatorIndex).trim();
        remainder = displayName.slice(separatorIndex + 3).trim();
      } else {
        heading = GENERAL_SECTION;
        remainder = displayName;
      }
    }

    if (!sections.has(heading)) {
      sections.set(heading, new Map());
      sectionOrder.push(heading);
    }

    const subGroupMap = sections.get(heading)!;
    const separatorIndex = remainder.indexOf(" / ");
    const subHeading =
      separatorIndex >= 0
        ? remainder.slice(0, separatorIndex).trim()
        : undefined;
    const label =
      separatorIndex >= 0
        ? remainder.slice(separatorIndex + 3).trim()
        : remainder;

    if (!subGroupMap.has(subHeading)) {
      subGroupMap.set(subHeading, new Map());
    }
    const labelMap = subGroupMap.get(subHeading)!;
    if (!labelMap.has(label)) labelMap.set(label, new Set());
    const values = labelMap.get(label)!;
    for (const value of item.displayValue.split(";")) {
      const trimmed = value.trim();
      if (trimmed) values.add(trimmed);
    }
  }

  return sectionOrder.map((heading) => {
    const subGroupMap = sections.get(heading)!;
    const groups: SpecSubGroup[] = [];
    for (const [subHeading, labelMap] of subGroupMap.entries()) {
      const attributes = Array.from(labelMap.entries()).map(
        ([label, values]) => ({
          label,
          values: Array.from(values),
        }),
      );
      groups.push({ subHeading, attributes });
    }
    return { heading, groups };
  });
}
